/**
 * GuidedTour
 *
 * Listens for `jamcam:open-tour` window events (dispatched from the navbar
 * help dropdown) and runs a multi-phase tour:
 *   1. Overview dialog — the JamCam shpiel.
 *   2. "general"  Joyride pass — points out navbar items, works on any page.
 *   3. Bridge dialog — offers the Cam-specific walkthrough.
 *   4. "cam" Joyride pass — runs after navigating to /cam, waiting for the
 *      stage tabs to mount (does not wait for the camera feed itself).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Joyride, {
  ACTIONS,
  CallBackProps,
  EVENTS,
  STATUS,
  Step,
} from 'react-joyride';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import TourRoundedIcon from '@mui/icons-material/TourRounded';
import { useDevOverride } from '../core/entitlement/DevOverrideContext';

type TourPhase = 'idle' | 'overview' | 'general' | 'cam-prompt' | 'cam';

const generalSteps: Step[] = [
  {
    target: '[data-tour-id="nav-logo"]',
    placement: 'bottom-start',
    title: 'Home',
    content:
      'The JamCam logo brings you back to your dashboard, or the demo when you are signed out.',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="nav-pill"]',
    placement: 'bottom',
    title: 'Main navigation',
    content:
      'Use the left sidebar to switch between Dashboard, Cam, Learn, Agent and Settings. On the stage, reopen it with the menu button in the top-left corner.',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="nav-link-cam"]',
    placement: 'bottom',
    title: 'Cam',
    content:
      'Jumps straight to the stage with your active overlay so you can noodle freely on the fretboard.',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="nav-link-learn"]',
    placement: 'bottom',
    title: 'Learn',
    content:
      'The module library — guided solo jams, chord-progression drills, scale tutors and more.',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="nav-link-agent"]',
    placement: 'bottom',
    title: 'Agent',
    content:
      'Your AI band leader. Ask it to build a practice session or design a guided chord progression for any song.',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="nav-link-settings"]',
    placement: 'bottom',
    title: 'Settings',
    content:
      'Deeper customization — overlay colors, sizing, theme and account-wide preferences.',
    disableBeacon: true,
  },

  {
    target: '[data-tour-id="nav-help"]',
    placement: 'bottom-end',
    title: 'Help',
    content:
      'Open Getting started in the sidebar to relaunch this guided tour any time.',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="nav-account"]',
    placement: 'bottom-end',
    title: 'Account',
    content:
      'Open your account here for your profile, purchase status, and full unlock.',
    disableBeacon: true,
  },
];

const camSteps: Step[] = [
  {
    target: 'body',
    placement: 'center',
    title: 'Welcome to the stage',
    content:
      'This is your live mirror. JamCam tracks your fretboard in real time and paints overlays directly onto the strings you can see.',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="stage-panel-input"]',
    placement: 'right',
    title: 'Input tab',
    content: 'Developer diagnostics and the played-notes log live here.',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="stage-panel-audio"]',
    placement: 'right',
    title: 'Tuner',
    content:
      'Turn on the live tuner here. It listens through your microphone only while it is shown. Once it appears, click it for cents and frequency or drag its handle to move it.',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="stage-panel-tracking"]',
    placement: 'right',
    title: 'Tracking tab',
    content:
      'Check fretboard tracking, get positioning help, and reacquire your guitar.',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="stage-panel-display"]',
    placement: 'left',
    title: 'Display tab',
    content:
      'Everything overlay lives here: pick your scale and root note, toggle which notes get highlighted (scale notes, roots, intervals, names), dial in brightness, anti-jitter and note size, mirror the video feed, and show the live fretboard diagram.',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="stage-panel-module"]',
    placement: 'left',
    title: 'Module tab',
    content:
      'When you launch a learning module from Learn, this tab opens that lesson’s instructions and module-specific controls right here on the stage.',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="stage-fullscreen"]',
    placement: 'bottom-end',
    title: 'Fullscreen',
    content:
      'Use the fullscreen button beside the stage menu to give your guitar the whole screen. Press Escape to exit.',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="stage-media-toggles"]',
    placement: 'right',
    title: 'Mute & camera toggles',
    content:
      'Quickly mute audio or cut the camera feed without leaving the stage.',
    disableBeacon: true,
  },
  {
    target: 'body',
    placement: 'center',
    title: 'You’re ready!',
    content:
      'That’s the stage. Jump into Learn for guided modules, the Agent tab to spin up a custom session, or Settings to fine-tune your overlays.',
    disableBeacon: true,
  },
];

/**
 * Polls for a CSS selector to appear in the DOM, resolving when it does or
 * when the timeout elapses. Used to wait for the stage tabs to mount after
 * navigating to /cam — we explicitly do *not* wait for the camera feed
 * itself, just for the tour-target DOM nodes.
 */
const FIRST_VISIT_KEY = 'jamcam.guidedTourSeen';

function waitForSelector(selector: string, timeoutMs = 8000): Promise<boolean> {
  return new Promise((resolve) => {
    if (document.querySelector(selector)) {
      resolve(true);
      return;
    }
    let resolved = false;
    const observer = new MutationObserver(() => {
      if (document.querySelector(selector)) {
        resolved = true;
        observer.disconnect();
        resolve(true);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    window.setTimeout(() => {
      if (!resolved) {
        observer.disconnect();
        resolve(false);
      }
    }, timeoutMs);
  });
}

/**
 * Kicks off the cam-phase by navigating to /cam (if needed), waiting for the
 * stage tabs to mount, and then dispatching a `jamcam:open-tour` event with
 * `{ tour: 'cam' }` so Joyride only mounts once the stage targets exist.
 */
async function launchCamTourViaEvent(): Promise<void> {
  const path = window.location.pathname;
  // The /demo route renders the same JamCamStage and doesn't require auth,
  // so if the user is already on /demo, keep them there for the cam tour
  // instead of redirecting to the protected /cam route.
  const isStageRoute = path === '/cam' || path === '/demo';
  if (!isStageRoute) {
    // Let the React Router setup handle navigation by dispatching a popstate.
    window.history.pushState({}, '', '/cam');
    window.dispatchEvent(new PopStateEvent('popstate'));
  }
  await waitForSelector('[data-tour-id="stage-panel-display"]', 8000);
  // Give React one frame to settle the new layout before mounting Joyride.
  window.requestAnimationFrame(() => {
    window.dispatchEvent(
      new CustomEvent('jamcam:open-tour', { detail: { tour: 'cam' } })
    );
  });
}

export function GuidedTour() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const devOverride = useDevOverride();
  const showInputPanelTab =
    devOverride.isLocalhost && devOverride.developerMode;
  const visibleCamSteps = useMemo(
    () =>
      showInputPanelTab
        ? camSteps
        : camSteps.filter(
            (step) => step.target !== '[data-tour-id="stage-panel-input"]'
          ),
    [showInputPanelTab]
  );
  const [phase, setPhase] = useState<TourPhase>('idle');
  const [stepIndex, setStepIndex] = useState(0);

  // Listen for navbar dispatches and direct-phase requests.
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ tour?: string }>).detail;
      const tour = detail?.tour ?? 'overview';
      if (tour === 'cam') {
        setStepIndex(0);
        setPhase('cam');
      } else if (tour === 'general') {
        setStepIndex(0);
        setPhase('general');
      } else {
        setStepIndex(0);
        setPhase('overview');
      }
    };
    window.addEventListener('jamcam:open-tour', handler);
    return () => window.removeEventListener('jamcam:open-tour', handler);
  }, []);

  // First-visit auto-launch.
  useEffect(() => {
    let seen: string | null = null;
    try {
      seen = window.localStorage.getItem(FIRST_VISIT_KEY);
    } catch {
      // localStorage may be unavailable (private mode, etc.) — bail.
      return;
    }
    if (seen) return;
    // Defer so route + layout have a chance to mount. We intentionally do
    // NOT write the "seen" flag until the timeout fires, because React 18
    // StrictMode runs effects twice in dev — writing synchronously would
    // cause the second mount to read the flag and skip the launch.
    const id = window.setTimeout(() => {
      try {
        window.localStorage.setItem(FIRST_VISIT_KEY, '1');
      } catch {
        // ignore
      }
      setStepIndex(0);
      setPhase('overview');
    }, 400);
    return () => window.clearTimeout(id);
  }, []);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent('jamcam:navigation', {
        detail: { open: phase === 'general', touring: phase === 'general' },
      })
    );
  }, [phase]);

  const startGeneralTour = useCallback(() => {
    setStepIndex(0);
    setPhase('general');
  }, []);

  const startCamTour = useCallback(() => {
    // Close the bridge dialog immediately.
    setPhase('idle');
    setStepIndex(0);
    // The /demo route hosts an unauthenticated copy of the stage. Keep the
    // user there if they're already on /demo so we don't bounce them into
    // the protected /cam route mid-tour.
    const isStageRoute =
      location.pathname === '/cam' || location.pathname === '/demo';
    if (!isStageRoute) {
      navigate('/cam');
    }
    // Hand off to the event-bus launcher, which waits for the stage to mount.
    void launchCamTourViaEvent();
  }, [navigate, location.pathname]);

  const handleClose = useCallback(() => {
    setPhase('idle');
    setStepIndex(0);
  }, []);

  const makeCallback = useCallback(
    (onFinish: () => void) => (data: CallBackProps) => {
      const { status, type, index, action } = data;
      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        onFinish();
        return;
      }
      if (action === ACTIONS.CLOSE) {
        handleClose();
        return;
      }
      if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
        const next = index + (action === ACTIONS.PREV ? -1 : 1);
        setStepIndex(Math.max(0, next));
      }
    },
    [handleClose]
  );

  const handleGeneralCallback = useMemo(
    () => makeCallback(() => setPhase('cam-prompt')),
    [makeCallback]
  );

  const handleCamCallback = useMemo(
    () => makeCallback(handleClose),
    [makeCallback, handleClose]
  );

  const joyrideStyles = useMemo(
    () => ({
      options: {
        zIndex: theme.zIndex.modal + 50,
        primaryColor: '#3670da',
        textColor: '#e6ecf8',
        backgroundColor: '#0f1326',
        arrowColor: '#0f1326',
        overlayColor: 'rgba(2,5,14,0.72)',
      },
      tooltip: {
        borderRadius: 6,
        padding: 16,
      },
      tooltipTitle: {
        fontSize: 16,
        fontWeight: 700,
      },
      tooltipContent: {
        fontSize: 14,
        lineHeight: 1.5,
      },
      buttonNext: {
        backgroundColor: '#3670da',
        borderRadius: 999,
        padding: '8px 18px',
      },
      buttonBack: {
        color: '#9ab0d8',
        marginRight: 8,
      },
      buttonSkip: {
        color: '#9ab0d8',
      },
    }),
    [theme]
  );

  const dialogPaperSx = {
    background:
      'linear-gradient(140deg, rgba(12,16,34,0.98), rgba(20,28,54,0.96))',
    border: '1px solid rgba(143,155,181,0.28)',
    color: 'rgba(230,236,248,0.92)',
  };

  return (
    <>
      {/* Overview dialog */}
      <Dialog
        open={phase === 'overview'}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TourRoundedIcon sx={{ color: '#82b0ed' }} />
          Welcome to JamCam
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: 'rgba(143,155,181,0.18)' }}>
          <Stack spacing={1.5}>
            <Typography variant="body2">
              JamCam is your live, mirror-style guitar coach. Point your camera
              at your fretboard and we’ll paint scales, intervals and lesson
              cues directly onto the strings you can already see.
            </Typography>
            <Typography variant="body2">
              JamCam works best on a laptop or desktop — the bigger screen makes
              the fretboard overlay much easier to see than on a phone.
            </Typography>
            <Typography variant="body2">
              <strong>Learn</strong> opens the module library — guided solo
              jams, chord-progression drills and focused scale tutors. Just want
              to noodle? <strong>Cam</strong> drops you straight on the stage
              with the active overlay.
            </Typography>
            <Typography variant="body2">
              The <strong>Agent</strong> tab is your AI band leader: it can spin
              up custom practice sessions or design a guided chord progression
              for any song.
            </Typography>
            <Typography variant="body2">
              <strong>Settings</strong> handles deeper customization (overlay
              colors, sizing, theme). Your account and one-time full unlock live
              under the avatar in the top right.
            </Typography>
            <Typography variant="body2">
              Want a quick guided tour of the interface?
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} sx={{ color: 'rgba(195,215,255,0.7)' }}>
            Maybe later
          </Button>
          <Button
            variant="contained"
            onClick={startGeneralTour}
            startIcon={<TourRoundedIcon />}
            sx={{ borderRadius: 999, px: 2.5 }}
          >
            Take the tour
          </Button>
        </DialogActions>
      </Dialog>

      {/* General nav tour */}
      {phase === 'general' && (
        <Box>
          <Joyride
            steps={generalSteps}
            stepIndex={stepIndex}
            run
            continuous
            showSkipButton
            showProgress
            disableScrolling
            scrollToFirstStep={false}
            spotlightPadding={6}
            callback={handleGeneralCallback}
            styles={joyrideStyles}
            locale={{
              back: 'Back',
              close: 'Close',
              last: 'Next',
              next: 'Next',
              skip: 'Skip tour',
            }}
          />
        </Box>
      )}

      {/* Bridge: continue to cam tour? */}
      <Dialog
        open={phase === 'cam-prompt'}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TourRoundedIcon sx={{ color: '#82b0ed' }} />
          Tour the Cam stage?
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: 'rgba(143,155,181,0.18)' }}>
          <Typography variant="body2">
            The Cam page has its own set of tabs and controls — overlays,
            tracking, audio, and a live tuner. Want a quick walkthrough?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} sx={{ color: 'rgba(195,215,255,0.7)' }}>
            I’m good
          </Button>
          <Button
            variant="contained"
            onClick={startCamTour}
            startIcon={<TourRoundedIcon />}
            sx={{ borderRadius: 999, px: 2.5 }}
          >
            Take me there
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cam tour */}
      {phase === 'cam' && (
        <Box>
          <Joyride
            steps={visibleCamSteps}
            stepIndex={stepIndex}
            run
            continuous
            showSkipButton
            showProgress
            disableScrolling
            scrollToFirstStep={false}
            spotlightPadding={6}
            callback={handleCamCallback}
            styles={joyrideStyles}
            locale={{
              back: 'Back',
              close: 'Close',
              last: 'Done',
              next: 'Next',
              skip: 'Skip tour',
            }}
          />
        </Box>
      )}
    </>
  );
}

export default GuidedTour;
